"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

type LoginResult = {
  ok?: boolean;
  next?: string;
  error?: string;
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loggarIn, setLoggarIn] = useState(false);

  // Tvinga login-flödet till www-host innan formuläret används.
  // Vercel 307-redirectar apex (vektr.se) → www.vektr.se, men cross-origin
  // POST i fetch() gör att Set-Cookie inte propagerar till browsern på
  // ett sätt som apex-side rendering ser. Vi gör en hård replace till www
  // så hela login-flödet sker på samma origin som cookien sätts på.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hostname === "vektr.se") {
      window.location.replace(
        `https://www.vektr.se${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = (await res.json().catch(() => ({}))) as LoginResult;

    if (!res.ok || data.error) {
      setLoading(false);
      setError(data.error ?? "Något gick fel. Försök igen.");
      return;
    }
    if (data.ok) {
      setLoggarIn(true);
      window.location.assign(data.next || "/");
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <label htmlFor="email" className="block">
            <span className="block text-sm font-medium mb-2 text-[var(--color-text)]">
              E-post
            </span>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              placeholder="din@email.se"
              className="w-full px-4 py-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-sm focus:outline-none focus:border-[var(--color-accent)]"
            />
          </label>

          <label htmlFor="password" className="block">
            <span className="block text-sm font-medium mb-2 text-[var(--color-text)]">
              Lösenord
            </span>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-sm focus:outline-none focus:border-[var(--color-accent)]"
            />
          </label>

          {error && (
            <p className="text-sm text-[var(--color-warning)]">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || loggarIn}
            className="w-full py-3 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            title="Logga in med din e-post och ditt lösenord"
          >
            {loggarIn
              ? "Loggar in..."
              : loading
                ? "Kontrollerar..."
                : "Logga in"}
          </button>

          <div className="flex items-center justify-start pt-2">
            <Link
              href="/glomt-losenord"
              className="text-sm text-[var(--color-text-muted)] hover:underline"
              title="Få en återställningslänk skickad till din e-post"
            >
              Glömt lösenord?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
